import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { privacyPolicyUrl } from '@/content/site';
import {
  acceptAllCookies,
  acceptEssentialOnly,
  closeConsentSettings,
  getConsent,
  hasStoredConsent,
  openConsentSettings,
  setConsent,
  subscribeConsent,
  subscribeConsentSettings,
  type CookiePreferences,
} from '@/lib/cookieConsent';
import { applyConsentServices } from '@/lib/consentServices';
import './CookieBanner.css';

type ModalView = 'intro' | 'settings';

function CookieModalShell({
  title,
  children,
  onBackdropClick,
}: {
  title: string;
  children: ReactNode;
  onBackdropClick?: () => void;
}) {
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = dialogRef.current;
    el?.focus();
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  return (
    <div
      className="cookie-modal__backdrop"
      role="presentation"
      onClick={onBackdropClick}
    >
      <div
        ref={dialogRef}
        className="cookie-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cookie-modal-title"
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="cookie-modal-title" className="cookie-modal__title">
          {title}
        </h2>
        {children}
      </div>
    </div>
  );
}

function CookieSettingsContent({
  initial,
  onSave,
  onBack,
  showBack,
}: {
  initial: CookiePreferences;
  onSave: (prefs: CookiePreferences) => void;
  onBack?: () => void;
  showBack?: boolean;
}) {
  const [prefs, setPrefs] = useState(initial);

  return (
    <>
      <p className="cookie-modal__lead">
        Elige qué categorías de almacenamiento local quieres permitir. Las esenciales son necesarias
        para recordar tu preferencia.
      </p>

      <div className="cookie-modal__category cookie-modal__category--locked">
        <div className="cookie-modal__category-head">
          <strong>Esenciales</strong>
          <span className="cookie-modal__always">Siempre activas</span>
        </div>
        <p>Recordar tu elección de cookies y mantener la seguridad básica del sitio.</p>
      </div>

      <label className="cookie-modal__category">
        <div className="cookie-modal__category-head">
          <strong>Analíticas</strong>
          <input
            type="checkbox"
            checked={prefs.analytics}
            onChange={(e) => setPrefs((p) => ({ ...p, analytics: e.target.checked }))}
          />
        </div>
        <p>
          Visitas, clics en CTAs y conversiones (incl. Google Analytics / Google Ads) para medir
          campañas y mejorar la landing.
        </p>
      </label>

      <label className="cookie-modal__category">
        <div className="cookie-modal__category-head">
          <strong>Funcionales</strong>
          <input
            type="checkbox"
            checked={prefs.functional}
            onChange={(e) => setPrefs((p) => ({ ...p, functional: e.target.checked }))}
          />
        </div>
        <p>Widget de chat en vivo y preferencias de sesión.</p>
      </label>

      <div className="cookie-modal__actions">
        {showBack && onBack ? (
          <button type="button" className="btn btn-secondary" onClick={onBack}>
            Volver
          </button>
        ) : null}
        <button type="button" className="btn btn-primary" onClick={() => onSave(prefs)}>
          Guardar preferencias
        </button>
      </div>
    </>
  );
}

export function CookieBanner() {
  const [needsConsent, setNeedsConsent] = useState(() => !hasStoredConsent());
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [view, setView] = useState<ModalView>('intro');
  const privacyUrl = privacyPolicyUrl();

  const handleConsent = useCallback((prefs: CookiePreferences) => {
    const stored = setConsent(prefs);
    applyConsentServices(stored);
    setNeedsConsent(false);
    setSettingsOpen(false);
    setView('intro');
  }, []);

  useEffect(() => {
    const existing = getConsent();
    if (existing) applyConsentServices(existing);

    return subscribeConsent((consent) => {
      setNeedsConsent(consent === null);
    });
  }, []);

  useEffect(() => {
    return subscribeConsentSettings((open) => {
      setSettingsOpen(open);
      if (open) setView('settings');
    });
  }, []);

  useEffect(() => {
    const onOpenSettings = () => {
      openConsentSettings();
      setSettingsOpen(true);
      setView('settings');
    };
    window.addEventListener('it-open-cookie-settings', onOpenSettings);
    return () => window.removeEventListener('it-open-cookie-settings', onOpenSettings);
  }, []);

  const showIntro = needsConsent && !settingsOpen;
  const showSettings = settingsOpen || (needsConsent && view === 'settings');

  if (!showIntro && !showSettings) return null;

  const currentPrefs: CookiePreferences = getConsent() ?? { analytics: false, functional: false };

  if (showIntro && view === 'intro') {
    return (
      <CookieModalShell title="Tu privacidad es importante">
        <p className="cookie-modal__lead">
          Usamos almacenamiento local para analítica anónima. Puedes aceptar todas, solo las esenciales
          o configurar tus preferencias. Más info en nuestra{' '}
          <a href={privacyUrl} target="_blank" rel="noopener noreferrer">
            política de privacidad
          </a>
          .
        </p>
        <div className="cookie-modal__actions cookie-modal__actions--intro">
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => {
              const stored = acceptAllCookies();
              applyConsentServices(stored);
              setNeedsConsent(false);
            }}
          >
            Aceptar todas
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => {
              const stored = acceptEssentialOnly();
              applyConsentServices(stored);
              setNeedsConsent(false);
            }}
          >
            Solo esenciales
          </button>
          <button type="button" className="btn btn-secondary" onClick={() => setView('settings')}>
            Configurar
          </button>
        </div>
      </CookieModalShell>
    );
  }

  return (
    <CookieModalShell
      title="Configurar cookies"
      onBackdropClick={
        hasStoredConsent()
          ? () => {
              closeConsentSettings();
              setSettingsOpen(false);
              setView('intro');
            }
          : undefined
      }
    >
      <CookieSettingsContent
        initial={currentPrefs}
        onSave={handleConsent}
        showBack={needsConsent}
        onBack={() => setView('intro')}
      />
    </CookieModalShell>
  );
}

export function triggerCookieSettings(): void {
  window.dispatchEvent(new Event('it-open-cookie-settings'));
}
